import axios from 'axios';
import { IDocument } from '@/models/document';
import { authenticatedApi } from '../api';
import { downloadBlobResponse } from '@/utils/fileDownload';

export interface DocumentConfig {
  type: string;
  label: string;
  description: string;
  mandatory: boolean;
  multiple: boolean;
  maxFiles?: number;
  acceptedFormats: string[];
  maxSizePerFile?: number; // in bytes
}


/**
 * API pour la gestion des documents SAV V2
 */
export const savV2DocumentApi = {
  /**
   * Récupère la configuration des documents pour un SAV
   */
  async getDocumentsConfig(savId: number): Promise<DocumentConfig[]> {
    const response = await authenticatedApi.get(`/sav-v2/${savId}/documents-config`);
    return response.data;
  },

  /**
   * Récupère tous les documents d'un SAV
   */
  async getBySavId(savId: number): Promise<IDocument[]> {
    const response = await authenticatedApi.get(`/sav-v2/${savId}/documents`);
    return response.data;
  },

  /**
   * Upload un document pour un SAV
   */
  async upload(
    savId: number,
    file: File,
    documentType: string,
    onProgress?: (progress: number) => void
  ): Promise<IDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', documentType);

    const response = await authenticatedApi.post(
      `/sav-v2/${savId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
        timeout: 60000, // 60 secondes pour l'upload
      }
    );

    return response.data;
  },

  /**
   * Upload multiple documents pour un SAV
   */
  async uploadMultiple(
    savId: number,
    files: { file: File; type: string }[],
    onProgress?: (progress: number) => void
  ): Promise<IDocument[]> {
    const formData = new FormData();

    files.forEach(({ file, type }, index) => {
      formData.append('files', file);
      formData.append(`types[${index}]`, type);
    });

    const response = await authenticatedApi.post(
      `/sav-v2/${savId}/documents/multiple`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
        timeout: 120000, // 120 secondes pour uploads multiples
      }
    );

    return response.data;
  },

  /**
   * Supprime un document
   */
  async delete(savId: number, documentId: number): Promise<void> {
    await authenticatedApi.delete(`/sav-v2/${savId}/documents/${documentId}`);
  },

  /**
   * Supprime un document d'un draft
   */
  async deleteDraftDocument(draftId: number, documentId: number): Promise<void> {
    await authenticatedApi.delete(`/sav-v2/${draftId}/draft/step4-document/${documentId}`);
  },

  /**
   * Récupère l'URL de téléchargement d'un document
   */
  async getDownloadUrl(savId: number, documentId: number): Promise<string> {
    const response = await authenticatedApi.get(`/sav-v2/${savId}/documents/${documentId}/download-url`);
    return response.data.url;
  },

  /**
   * Récupère l'URL pour visualiser un document
   */
  async getDocumentUrl(savId: number, documentId: number): Promise<string> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/documents/${documentId}/get-download-token`
    );
    const token = response.data.documentToken;
    return `${authenticatedApi.defaults.baseURL}/documents/${documentId}/view?documentDownloadToken=${token}`;
  },

  /**
   * Télécharge un document. Téléchargé en blob (un lien `download` vers le
   * domaine de l'API est cross-origin : le navigateur ignorerait le nom).
   * Sans `fileName`, le nom est celui du serveur (libellé + extension réelle) ;
   * avec, seule sa partie lisible est reprise, l'extension reste celle du serveur.
   */
  async downloadDocument(savId: number, documentId: number, fileName?: string): Promise<void> {
    const response = await authenticatedApi.get(
      `/sav-v2/${savId}/documents/${documentId}/get-download-token`
    );
    const token = response.data.documentToken;
    // Instance axios nue : l'accès est porté par le token en query, et un 401
    // (token de 10 s expiré) ne doit pas déclencher le logout global de
    // `authenticatedApi`.
    const fileResponse = await axios.get<Blob>(
      `/documents/${documentId}/download?downloadType=download&documentDownloadToken=${token}`,
      { baseURL: authenticatedApi.defaults.baseURL, responseType: 'blob' }
    );
    downloadBlobResponse(fileResponse, fileName);
  },
};